import type { Envelope } from "./types";
import { demoEnvelope, demoJobs, demoMilestones, demoOverview } from "./demo";

const demo = new URLSearchParams(window.location.search).get("demo") === "1" || import.meta.env.VITE_DEMO === "1";
const now = new Date().toISOString();
const localAgentBases = window.location.hostname === "tauri.localhost"
  ? ["http://127.0.0.1:8765", "http://127.0.0.1:8767"]
  : [""];
let desktopToken: Promise<string> | null = null;

async function authToken(): Promise<string> {
  const invoke = (window as any).__TAURI_INTERNALS__?.invoke;
  if (!invoke) return localStorage.getItem("control-center-token") ?? "";
  desktopToken ??= invoke("agent_token").then((value: string | null) => value ?? "").catch(() => "");
  return desktopToken!;
}

export const agentAuthToken = authToken;

async function agentFetch(path: string, init?: RequestInit): Promise<Response> {
  let lastError: unknown;
  for (const base of localAgentBases) {
    try {
      return await fetch(`${base}${path}`, init);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError ?? new Error("AGENT_UNAVAILABLE");
}

const demoRoutes: Record<string, Envelope<unknown>> = {
  "/v1/overview": demoOverview,
  "/v1/jobs": demoEnvelope(demoJobs),
  "/v1/milestones": demoEnvelope(demoMilestones),
  "/v1/schedules": demoEnvelope({ paused: 0, timezone: "America/Denver", version: 3, slots: [
    { slot_id: "mon", day_code: "mon", local_time: "09:00", enabled: 1 },
    { slot_id: "wed", day_code: "wed", local_time: "09:00", enabled: 1 },
    { slot_id: "fri", day_code: "fri", local_time: "09:00", enabled: 1 }
  ] }),
  "/v1/content": demoEnvelope([
    { video_id: "2026-08-22-the-boring-billion", stage_status: "ok", updated_at: "2026-08-22T15:18:00Z", upload_status: "private_unscheduled", views: 18420 },
    { video_id: "2026-08-21-hycean-worlds", stage_status: "ok", updated_at: "2026-08-21T15:22:00Z", upload_status: "private_unscheduled", views: 12763 }
  ]),
  "/v1/activity": demoEnvelope([
    { id: 3, category: "success", event_type: "job.completed", occurred_at: now, message: "Video uploaded privately to YouTube." },
    { id: 2, category: "info", event_type: "analytics.refreshed", occurred_at: now, message: "Analytics refreshed." },
    { id: 1, category: "warning", event_type: "recovery.retry", occurred_at: now, message: "Visual provider timed out; retry scheduled." }
  ]),
  "/v1/settings": demoEnvelope({ "ui.accent": "signal", "ui.default_page": "Overview", "ui.auto_refresh_on_open": true }),
  "/v1/analytics?range_key=lifetime": demoEnvelope({ captured_at: now, payload: {
    dates: ["2026-05-01", "2026-06-01", "2026-07-01", "2026-08-01"], cumulative_views: [12000, 33000, 67000, 128430],
    cumulative_watch_hours: [90, 220, 430, 712], cumulative_subscribers: [31, 88, 176, 327]
  } })
};

export async function api<T>(path: string, init?: RequestInit): Promise<Envelope<T>> {
  if (demo && demoRoutes[path]) return structuredClone(demoRoutes[path]) as Envelope<T>;
  const token = await authToken();
  const response = await agentFetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(token ? { "X-Control-Center-Token": token } : {}), ...init?.headers }
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.detail?.code ?? `HTTP_${response.status}`);
  }
  return response.json();
}

export async function confirmedAction(action: string, path: string, body: unknown = {}): Promise<Envelope<unknown>> {
  const confirmation = await api<{ token: string }>("/v1/confirmations", { method: "POST", body: JSON.stringify({ action }) });
  return api(path, {
    method: "POST", body: JSON.stringify(body),
    headers: { "Idempotency-Key": crypto.randomUUID(), "X-Confirmation-Token": confirmation.data.token }
  });
}

export async function download(path: string, filename: string): Promise<void> {
  const token = await authToken();
  const response = await agentFetch(path, { headers: token ? { "X-Control-Center-Token": token } : {} });
  if (!response.ok) throw new Error(`HTTP_${response.status}`);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function enrollDesktopAgent(repoRoot: string): Promise<string> {
  const invoke = (window as any).__TAURI_INTERNALS__?.invoke;
  if (!invoke) throw new Error("DESKTOP_ONLY");
  const enrolled = await invoke("enroll_agent", { repoRoot });
  desktopToken = null;
  return enrolled;
}

export { demo };
